import axiosInst from '@/utils/axiosinstance';
import { UAParser } from 'ua-parser-js';
import { API_ORIGIN } from '@/config/apiBase';

/**
 * Fire-and-forget page-visit tracking for the admin Users report.
 *
 * Records the visit into the collection behind the matching admin tab:
 *   - logged-in user  → POST /api/adminreport  → "Authenticated Users" tab
 *   - guest           → POST /api/visitors     → "Unauthenticated Users" tab
 *
 * This is the report-level counterpart to trackInteraction('page-view', …),
 * which logs every navigation into interactionEvents for the Interaction
 * Analytics page. Each call here costs a server-side ip-api location lookup,
 * so wire it into specific page mounts (Dashboard, View Ads, Messages, …),
 * NOT into a global router hook. Never throws and never blocks the page.
 *
 * Usage:
 *   import { trackPageVisit } from '@/utils/trackPageVisit';
 *   trackPageVisit('Messages');
 *   trackPageVisit('View Ads', { serviceSlug: this.serviceName, servicesID: this.servicesID });
 *
 * @param {string} pageName  Label shown in the admin tab's Page column.
 * @param {{serviceSlug?: string, servicesID?: string, ip?: string}} [opts]  Listing
 *   identity, only meaningful for listing detail views (links the row to
 *   /view-post/:slug). Pass `ip` when the caller already looked it up.
 */
export async function trackPageVisit(pageName, opts = {}) {
  try {
    const visitData = await buildVisitData(pageName, opts.ip);

    const userID = localStorage.getItem('userid');
    if (userID) {
      await axiosInst.post('/api/adminreport', {
        ...visitData,
        userID,
        userName: localStorage.getItem('username') || '',
      });
    } else {
      await axiosInst.post('/api/visitors', {
        ...visitData,
        serviceSlug: opts.serviceSlug || null,
        servicesID: opts.servicesID || null,
      });
    }
  } catch (_) {
    /* tracking must never surface an error */
  }
}

/**
 * Record a successful login in the admin "Authenticated Users" tab.
 *
 * Every login path (email/password, email link, Google, Facebook) calls this,
 * so the tab no longer depends on which button the user signed in with —
 * Google and Facebook logins used to write nothing at all.
 *
 * Login flows usually reload or route away straight afterwards, which used to
 * abort the report request mid-flight. So the IP lookup is capped and the
 * report is sent with `keepalive`, which the browser finishes even after the
 * page unloads. Await it before navigating; it resolves within ~1.5s.
 *
 * @param {'Email' | 'Email Link' | 'Google' | 'Facebook'} method
 * @param {{userID: string, userName?: string}} user
 */
export async function trackLogin(method, { userID, userName } = {}) {
  try {
    if (!userID) return;
    const visitData = await buildVisitData(`Login (${method})`);
    const body = JSON.stringify({ ...visitData, userID, userName: userName || '' });
    fetch(`${API_ORIGIN}/api/adminreport`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body,
    }).catch(() => {});
  } catch (_) {
    /* tracking must never surface an error */
  }
}

// Browser/device details plus the public IP. The IP lookup is capped at
// 1.5s; when it fails the server falls back to the request's own IP.
async function buildVisitData(pageName, knownIp) {
  const browserInfo = new UAParser().getResult();
  return {
    ip: knownIp || (await lookupIp()),
    timestamp: new Date().toISOString(),
    pageName,
    browser: browserInfo.browser.name,
    browserVersion: browserInfo.browser.version,
    os: browserInfo.os.name,
    osVersion: browserInfo.os.version,
    device: browserInfo.device.type || 'Desktop',
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

// One lookup per page load, shared by every tracker. A failed lookup isn't
// cached, so the next caller tries again.
let ipPromise = null;

function lookupIp() {
  if (!ipPromise) {
    ipPromise = fetchIp().then((ip) => {
      if (!ip) ipPromise = null;
      return ip;
    });
  }
  return ipPromise;
}

// Start the lookup early (e.g. when the login modal opens) so trackLogin
// doesn't wait on it at submit time.
export function prefetchIp() {
  lookupIp();
}

async function fetchIp() {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 1500);
    const res = await fetch('https://api64.ipify.org?format=json', { signal: ctrl.signal });
    clearTimeout(timer);
    return (await res.json()).ip || undefined;
  } catch (_) {
    return undefined;
  }
}

export default trackPageVisit;
